import React, { memo, useEffect, useMemo } from 'react'
import { IDateTimeProps } from '../../../../../DateTime/DateTime'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import {
    selectRuleConfigBit,
    selectRuleIsChecked,
} from '../../../../../../../store/selectors/ruleCardSelectors'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { datePickerErrors } from '../../../../../../../utils/validationErrors/datePickerErrors'
import { setTimeFormat } from '../../../../../DateTime/utils'
import { ruleCardActions } from '../../../../../../../store/slices/ruleCardSlice'
import {
    initRuleEmptyData,
    modifyRuleCardConfig,
} from '../../../../../../../store/actions/ruleCardActions'
import _isEqual from 'lodash/isEqual'
import {
    buildControlsExt,
    dateTimePicker,
} from '../../../../../../inputs/controls'

export const RuleDateTime = memo(function RuleDateTime({
    dataPath,
    pathWithElementID,
    dataSchema = {},
    name,
    isParentDisabled,
}: IDateTimeProps) {
    const dispatch = useAppDispatch()
    const {
        greater_or_equal,
        greater_than,
        less_or_equal,
        less_than,
        default: defaultValue,
        format: timeSchema,
        description,
        is_required: isRequired,
        type,
        time_source: timeSource,
        change_warning: changeWarning,
    } = dataSchema
    const pathSplit = useMemo(
        () => (dataPath?.length ? dataPath.split(PATH_JOINER) : []),
        [dataPath]
    )
    const path = useMemo(() => [...pathSplit, name], [pathSplit, name])
    const configBit = useAppSelector(selectRuleConfigBit(pathSplit))
    const errorKey = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)

    const isChecked = useAppSelector(selectRuleIsChecked)
    const validationCheck = inputValidationCheck(type, dataSchema)
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit, { [name]: validationCheck })

    const minDate = greater_or_equal || greater_than
    const maxDate = less_or_equal || less_than
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'

    const errMsg = datePickerErrors(inputState?.[name], dataSchema)
    const timeFormat = setTimeFormat(timeSchema)

    // This effect highlights invalid input on Apply
    useEffect(() => {
        const isRuleConfigChecked = isChecked
        const isParentBlockNotDisabled /* has non-empty path */ =
            dataPath.length
        if (isRuleConfigChecked && isParentBlockNotDisabled) {
            isValid()
        }
    }, [isChecked, dataPath.length])

    useEffect(() => {
        if (errMsg && path.length > 1) {
            dispatch(
                ruleCardActions.setRuleConfigInvalid({ errorKeyName: errorKey })
            )
        }
        if (!errMsg && path.length > 1) {
            dispatch(
                ruleCardActions.setRuleConfigValid({ errorKeyName: errorKey })
            )
        }
    }, [inputState?.[name], dispatch])

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            if (!configBit || !isDataEmpty || isUndefined) {
                dispatch(initRuleEmptyData({ path, name, schema: dataSchema }))
            }
            if (defaultValue && !configBit[name]) {
                setInputState({ ...configBit, [name]: defaultValue })
            }
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            if (!configBit || !isDataEmpty || isUndefined) {
                dispatch(initRuleEmptyData({ path, name, schema: dataSchema }))
            }
            if (!_isEqual(configBit, inputState)) {
                dispatch(modifyRuleCardConfig({ path, data: inputState }))
            }
        }
    }, [inputState, touched]) // eslint-disable-line

    const controlDescriptors = [
        dateTimePicker(
            name,
            {
                timeFormat,
                timeSchema,
                timeSource,
                description,
                isColumn: true,
                minDate,
                maxDate,
                errMsg,
                isParentDisabled,
                changeWarning,
            },
            isRequired
        ),
    ]

    const DateTimeInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className="w-100">{DateTimeInput}</div>
})
